import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import GlassCard from '../components/GlassCard';
import { familiesAPI, peopleAPI } from '../services/api';
import { parseDateComponents, getMonthNameShort, getMonthNameFull, getMonth, getDay, getYear } from '../utils/dateUtils';

const PeoplePage = () => {
  const { theme } = useTheme();
  const [people, setPeople] = useState([]);
  const [families, setFamilies] = useState([]);
  const [selectedFamily, setSelectedFamily] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddMemberForm, setShowAddMemberForm] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [newMember, setNewMember] = useState({
    first_name: '',
    last_name: '',
    preferred_name: '',
    date_of_birth: '',
    sex: 'unknown',
  });

  useEffect(() => {
    loadFamilies();
  }, []);

  useEffect(() => {
    if (selectedFamily) {
      loadPeople(selectedFamily);
    } else {
      setPeople([]);
    }
  }, [selectedFamily]);

  const loadFamilies = async () => {
    try {
      const response = await familiesAPI.list();
      setFamilies(response.data.families || []);
      if (response.data.families?.length > 0) {
        setSelectedFamily(response.data.families[0].id);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load families');
    }
  };

  const loadPeople = async (familyId) => {
    try {
      setLoading(true);
      const response = await peopleAPI.list(familyId);
      setPeople(response.data.people || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load members');
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    if (!selectedFamily) {
      setError('Please select a family first');
      return;
    }
    if (!newMember.first_name.trim()) {
      setError('First name is required');
      return;
    }
    try {
      await peopleAPI.create(selectedFamily, {
        first_name: newMember.first_name,
        last_name: newMember.last_name,
        preferred_name: newMember.preferred_name,
        date_of_birth: newMember.date_of_birth || null,
        sex: newMember.sex || 'unknown',
      });
      await loadPeople(selectedFamily);
      setShowAddMemberForm(false);
      setNewMember({ first_name: '', last_name: '', preferred_name: '', date_of_birth: '', sex: 'unknown' });
      setError(null);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add member');
    }
  };

  // Filter people based on search term
  const filteredPeople = people.filter((person) => {
    if (!searchTerm.trim()) return true;
    
    const searchLower = searchTerm.toLowerCase();
    
    // Search by name (first, last, preferred)
    const fullName = `${person.first_name || ''} ${person.last_name || ''} ${person.preferred_name || ''}`.toLowerCase();
    if (fullName.includes(searchLower)) return true;
    
    // Search by location (city, country)
    const location = `${person.city || ''} ${person.country || ''}`.toLowerCase();
    if (location.includes(searchLower)) return true;
    
    // Search by month and day of birth (e.g., "Aug 22", "8/22", "08-22")
    if (person.date_of_birth) {
      const components = parseDateComponents(person.date_of_birth);
      if (components) {
        const month = components.month; // 1-12
        const day = components.day; // 1-31
        const monthName = getMonthNameShort(person.date_of_birth); // "Aug"
        const monthNameFull = getMonthNameFull(person.date_of_birth); // "August"
        
        // Check various date formats
        if (
          `${month}/${day}`.includes(searchLower) ||
          `${month}-${day}`.includes(searchLower) ||
          `${String(month).padStart(2, '0')}/${String(day).padStart(2, '0')}`.includes(searchLower) ||
          `${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`.includes(searchLower) ||
          `${monthName} ${day}`.toLowerCase().includes(searchLower) ||
          `${monthNameFull} ${day}`.toLowerCase().includes(searchLower) ||
          `${day} ${monthName}`.toLowerCase().includes(searchLower) ||
          `${day} ${monthNameFull}`.toLowerCase().includes(searchLower)
        ) {
          return true;
        }
      }
    }
    
    return false;
  });

  return (
    <div>
      <div className="people-page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px', flexWrap: 'wrap', gap: '12px' }}>
        <h1 style={{ fontSize: 'clamp(24px, 5vw, 36px)', color: theme.text.primary, margin: 0 }}>Members</h1>
        {selectedFamily && (
          <button
            onClick={() => setShowAddMemberForm(!showAddMemberForm)}
            className="add-member-button"
            style={{
              padding: 'clamp(10px, 2vw, 12px) clamp(16px, 4vw, 24px)',
              borderRadius: '8px',
              border: theme.glass.border,
              background: theme.accent.primary,
              color: 'white',
              fontSize: 'clamp(14px, 3vw, 16px)',
              fontWeight: 600,
              cursor: 'pointer',
              minHeight: '44px',
              whiteSpace: 'nowrap',
            }}
          >
            + Add Member
          </button>
        )}
      </div>

      {/* Search Bar */}
      {selectedFamily && people.length > 0 && (
        <GlassCard style={{ marginBottom: '24px' }}>
          <input
            type="text"
            placeholder="Search by name, location, or birth date (e.g., Aug 22, 8/22)..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: 'clamp(10px, 2vw, 12px) clamp(12px, 3vw, 16px)',
              borderRadius: '8px',
              border: theme.glass.border,
              background: theme.card.background,
              color: theme.text.primary,
              fontSize: '16px',
              minHeight: '44px',
            }}
          />
        </GlassCard>
      )}

      {families.length > 0 && (
        <GlassCard style={{ marginBottom: '24px' }}>
          <label
            style={{
              display: 'block',
              marginBottom: '8px',
              color: theme.text.secondary,
              fontSize: 'clamp(13px, 2.5vw, 14px)',
            }}
          >
            Filter by Family:
          </label>
          <select
            value={selectedFamily || ''}
            onChange={(e) => setSelectedFamily(e.target.value)}
            style={{
              width: '100%',
              padding: 'clamp(10px, 2vw, 12px)',
              borderRadius: '8px',
              border: theme.glass.border,
              background: theme.card.background,
              color: theme.text.primary,
              fontSize: '16px',
              minHeight: '44px',
            }}
          >
            <option value="">All Families</option>
            {families.map((family) => (
              <option key={family.id} value={family.id}>
                {family.name}
              </option>
            ))}
          </select>
        </GlassCard>
      )}

      {showAddMemberForm && selectedFamily && (
        <GlassCard style={{ marginBottom: '24px' }} className="add-member-form-card">
          <h3 style={{ fontSize: 'clamp(18px, 4vw, 20px)', marginBottom: '16px', color: theme.text.primary }}>Add New Member</h3>
          <form onSubmit={handleAddMember} className="add-member-form">
            <div className="add-member-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <input
                type="text"
                placeholder="First Name *"
                value={newMember.first_name}
                onChange={(e) => setNewMember({ ...newMember, first_name: e.target.value })}
                required
                style={{
                  padding: 'clamp(10px, 2vw, 12px)',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: theme.card.background,
                  color: theme.text.primary,
                  fontSize: '16px',
                  minHeight: '44px',
                  width: '100%',
                }}
              />
              <input
                type="text"
                placeholder="Last Name"
                value={newMember.last_name}
                onChange={(e) => setNewMember({ ...newMember, last_name: e.target.value })}
                style={{
                  padding: 'clamp(10px, 2vw, 12px)',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: theme.card.background,
                  color: theme.text.primary,
                  fontSize: '16px',
                  minHeight: '44px',
                  width: '100%',
                }}
              />
            </div>
            <div className="add-member-form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px', marginBottom: '12px' }}>
              <input
                type="text"
                placeholder="Preferred Name"
                value={newMember.preferred_name}
                onChange={(e) => setNewMember({ ...newMember, preferred_name: e.target.value })}
                style={{
                  padding: 'clamp(10px, 2vw, 12px)',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: theme.card.background,
                  color: theme.text.primary,
                  fontSize: '16px',
                  minHeight: '44px',
                  width: '100%',
                }}
              />
              <input
                type="date"
                placeholder="Date of Birth"
                value={newMember.date_of_birth}
                onChange={(e) => setNewMember({ ...newMember, date_of_birth: e.target.value })}
                style={{
                  padding: 'clamp(10px, 2vw, 12px)',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: theme.card.background,
                  color: theme.text.primary,
                  fontSize: '16px',
                  minHeight: '44px',
                  width: '100%',
                }}
              />
            </div>
            <div style={{ marginBottom: '12px' }}>
              <label
                style={{
                  display: 'block',
                  marginBottom: '8px',
                  color: theme.text.secondary,
                  fontSize: 'clamp(13px, 2.5vw, 14px)',
                }}
              >
                Gender
              </label>
              <select
                value={newMember.sex}
                onChange={(e) => setNewMember({ ...newMember, sex: e.target.value })}
                style={{
                  width: '100%',
                  padding: 'clamp(10px, 2vw, 12px)',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: theme.card.background,
                  color: theme.text.primary,
                  fontSize: '16px',
                  minHeight: '44px',
                }}
              >
                <option value="unknown">Unknown</option>
                <option value="male">Male ♂</option>
                <option value="female">Female ♀</option>
              </select>
            </div>
            <div className="add-member-form-buttons" style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
              <button
                type="submit"
                style={{
                  padding: 'clamp(10px, 2vw, 12px) clamp(16px, 4vw, 20px)',
                  borderRadius: '6px',
                  border: 'none',
                  background: theme.accent.primary,
                  color: 'white',
                  fontSize: 'clamp(14px, 3vw, 16px)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  minHeight: '44px',
                  flex: '1 1 auto',
                  minWidth: '120px',
                }}
              >
                Add Member
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowAddMemberForm(false);
                  setNewMember({ first_name: '', last_name: '', preferred_name: '', date_of_birth: '', sex: 'unknown' });
                }}
                style={{
                  padding: 'clamp(10px, 2vw, 12px) clamp(16px, 4vw, 20px)',
                  borderRadius: '6px',
                  border: theme.glass.border,
                  background: 'transparent',
                  color: theme.text.primary,
                  fontSize: 'clamp(14px, 3vw, 16px)',
                  fontWeight: 600,
                  cursor: 'pointer',
                  minHeight: '44px',
                  flex: '1 1 auto',
                  minWidth: '120px',
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        </GlassCard>
      )}

      {error && (
        <GlassCard style={{ marginBottom: '24px', background: 'rgba(255, 0, 0, 0.1)' }}>
          <p style={{ color: theme.text.primary }}>Error: {error}</p>
        </GlassCard>
      )}

      {loading ? (
        <GlassCard style={{ textAlign: 'center', padding: '48px' }}>
          <p style={{ color: theme.text.secondary }}>Loading members...</p>
        </GlassCard>
      ) : people.length === 0 ? (
        <GlassCard style={{ textAlign: 'center', padding: '48px' }}>
          <p style={{ color: theme.text.secondary }}>No members found.</p>
        </GlassCard>
      ) : filteredPeople.length === 0 && searchTerm.trim() ? (
        <GlassCard style={{ textAlign: 'center', padding: '48px' }}>
          <p style={{ color: theme.text.secondary }}>No members found matching "{searchTerm}"</p>
        </GlassCard>
      ) : (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))',
            gap: '24px',
          }}
        >
          {filteredPeople.map((person) => {
            const genderColor = person.sex === 'male' ? 'rgba(37, 99, 235, 0.2)' : person.sex === 'female' ? 'rgba(234, 88, 12, 0.2)' : 'rgba(107, 114, 128, 0.2)';
            const borderColor = person.sex === 'male' ? 'rgba(37, 99, 235, 0.3)' : person.sex === 'female' ? 'rgba(234, 88, 12, 0.3)' : 'rgba(107, 114, 128, 0.3)';
            return (
              <Link
                key={person.id}
                to={`/people/${person.id}`}
                style={{ textDecoration: 'none', color: 'inherit' }}
              >
                <GlassCard
                  style={{
                    background: genderColor,
                    backdropFilter: 'blur(10px)',
                    WebkitBackdropFilter: 'blur(10px)',
                    border: `1px solid ${borderColor}`,
                  }}
                >
                  {person.picture_url && (
                    <img
                      src={person.picture_url}
                      alt={`${person.first_name} ${person.last_name || ''}`}
                      style={{
                        width: '100%',
                        height: '200px',
                        objectFit: 'cover',
                        borderRadius: '8px',
                        marginBottom: '12px',
                      }}
                    />
                  )}
                  <div style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '4px' }}>
                    <span style={{ fontSize: '16px', color: theme.text.primary }}>
                      {person.sex === 'male' ? '♂' : person.sex === 'female' ? '♀' : '⚥'}
                    </span>
                    <h3 style={{ fontSize: '16px', margin: 0, color: theme.text.primary, fontWeight: 600 }}>
                      {person.preferred_name || person.first_name} {person.last_name || ''}
                    </h3>
                  </div>
                  {person.date_of_birth && (
                    <p style={{ color: theme.text.muted, fontSize: '12px', margin: 0 }}>
                      {getYear(person.date_of_birth)}
                    </p>
                  )}
                </GlassCard>
              </Link>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default PeoplePage;

